import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, User, Tables } from "../utils/supabase";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  EyeIcon,
  CalendarDaysIcon,
  PhoneIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import { format, parseISO, subMonths, startOfMonth, endOfMonth, isWithinInterval } from "date-fns";
import { nl } from "date-fns/locale";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import toast from "react-hot-toast";
import { exportToCsv } from "../utils/exportCsv";
import ConfirmDialog from "../components/ConfirmDialog";
import Pagination from "../components/Pagination";
import { logAuditEvent } from "../utils/auditLog";

const COMPLAINTS_PAGE_SIZE = 20;

interface ComplaintWithUser {
  id: string;
  pharmacy_id: string;
  complaint_number: string;
  customer_name?: string;
  customer_contact?: string;
  description: string;
  category: "product" | "service" | "staff" | "other";
  severity: "low" | "medium" | "high" | "critical";
  status: "open" | "investigating" | "resolved" | "closed";
  assigned_to?: string;
  created_by: string;
  resolution?: string;
  resolved_at?: string;
  // CAPA fields
  root_cause?: string;
  corrective_action?: string;
  preventive_action?: string;
  capa_status?: "not_required" | "open" | "in_progress" | "completed";
  capa_due_date?: string;
  capa_responsible_id?: string;
  capa_responsible_name?: string;
  capa_completed_at?: string;
  created_at: string;
  updated_at: string;
  pharmacy_name: string;
  created_by_name: string;
  assigned_to_name?: string;
}

interface ComplaintFilters {
  status: string;
  severity: string;
  category: string;
  assignedTo: string;
  search: string;
}

const Complaints: React.FC = () => {
  const { user, selectedPharmacy } = useAuth();
  const [complaints, setComplaints] = useState<ComplaintWithUser[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] =
    useState<ComplaintWithUser | null>(null);
  const [filters, setFilters] = useState<ComplaintFilters>(() => {
    try {
      const saved = sessionStorage.getItem("complaints_filters");
      return saved ? JSON.parse(saved) : { status: "all", severity: "all", category: "all", assignedTo: "all", search: "" };
    } catch {
      return { status: "all", severity: "all", category: "all", assignedTo: "all", search: "" };
    }
  });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"list" | "trends">("list");

  // CAPA form state
  const [capaForm, setCapaForm] = useState({
    root_cause: "",
    corrective_action: "",
    preventive_action: "",
    capa_status: "open" as "not_required" | "open" | "in_progress" | "completed",
    capa_due_date: "",
    capa_responsible_id: "",
  });
  const [savingCapa, setSavingCapa] = useState(false);

  // Persist filters and reset page when filters change
  useEffect(() => {
    sessionStorage.setItem("complaints_filters", JSON.stringify(filters));
    setPage(1);
  }, [filters]);

  // New complaint form state
  const [newComplaint, setNewComplaint] = useState({
    customer_name: "",
    customer_contact: "",
    description: "",
    category: "other" as "product" | "service" | "staff" | "other",
    severity: "medium" as "low" | "medium" | "high" | "critical",
    assigned_to: "",
  });

  useEffect(() => {
    if (selectedPharmacy) {
      fetchComplaints();
      fetchUsers();
    }
  }, [selectedPharmacy]);

  const fetchComplaints = async () => {
    if (!selectedPharmacy) {
      return undefined;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from(Tables.COMPLAINTS)
        .select(
          `
          *,
          pharmacy:pharmacies(name),
          created_by_user:users!complaints_created_by_fkey(full_name),
          assigned_to_user:users!complaints_assigned_to_fkey(full_name)
        `,
        )
        .eq("pharmacy_id", selectedPharmacy.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching complaints:", error);
        toast.error("Fout bij het laden van klachten");
        return undefined;
      }

      const complaintsWithUsers: ComplaintWithUser[] = data.map(
        (complaint: any) => ({
          id: complaint.id,
          pharmacy_id: complaint.pharmacy_id,
          complaint_number: complaint.complaint_number,
          customer_name: complaint.customer_name,
          customer_contact: complaint.customer_contact,
          description: complaint.description,
          category: complaint.category,
          severity: complaint.severity,
          status: complaint.status,
          assigned_to: complaint.assigned_to,
          created_by: complaint.created_by,
          resolution: complaint.resolution,
          resolved_at: complaint.resolved_at,
          created_at: complaint.created_at,
          updated_at: complaint.updated_at,
          pharmacy_name: complaint.pharmacy?.name || "",
          created_by_name: complaint.created_by_user?.full_name || "",
          assigned_to_name: complaint.assigned_to_user?.full_name || "",
        }),
      );

      setComplaints(complaintsWithUsers);
    } catch (error) {
      console.error("Error fetching complaints:", error);
      toast.error("Fout bij het laden van klachten");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!selectedPharmacy) {
      return undefined;
    }

    try {
      const { data, error } = await supabase
        .from("user_pharmacies")
        .select(
          `
          user:users(*)
        `,
        )
        .eq("pharmacy_id", selectedPharmacy.id);

      if (error) {
        console.error("Error fetching users:", error);
        return undefined;
      }

      const userList = data.map((item: any) => item.user).filter(Boolean);
      setUsers(userList);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const generateComplaintNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const time =
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0");
    return `KL${year}${month}${day}-${time}`;
  };

  const createComplaint = async () => {
    if (!selectedPharmacy || !user) {
      return undefined;
    }

    if (!newComplaint.description.trim()) {
      toast.error("Beschrijving is verplicht");
      return undefined;
    }

    try {
      const complaintData = {
        pharmacy_id: selectedPharmacy.id,
        complaint_number: generateComplaintNumber(),
        customer_name: newComplaint.customer_name.trim() || null,
        customer_contact: newComplaint.customer_contact.trim() || null,
        description: newComplaint.description.trim(),
        category: newComplaint.category,
        severity: newComplaint.severity,
        status: "open" as const,
        assigned_to: newComplaint.assigned_to || null,
        created_by: user.id,
      };

      const { error } = await supabase
        .from(Tables.COMPLAINTS)
        .insert([complaintData]);

      if (error) {
        console.error("Error creating complaint:", error);
        toast.error("Fout bij het aanmaken van klacht");
        return undefined;
      }

      toast.success("Klacht succesvol aangemaakt");
      setShowCreateModal(false);
      setNewComplaint({
        customer_name: "",
        customer_contact: "",
        description: "",
        category: "other",
        severity: "medium",
        assigned_to: "",
      });
      fetchComplaints();
    } catch (error) {
      console.error("Error creating complaint:", error);
      toast.error("Fout bij het aanmaken van klacht");
    }
  };

  const updateComplaintStatus = async (
    complaintId: string,
    status: ComplaintWithUser["status"],
    resolution?: string,
  ) => {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString(),
      };

      if (status === "resolved" || status === "closed") {
        updateData.resolved_at = new Date().toISOString();
        if (resolution) {
          updateData.resolution = resolution;
        }
      }

      const { error } = await supabase
        .from(Tables.COMPLAINTS)
        .update(updateData)
        .eq("id", complaintId);

      if (error) {
        console.error("Error updating complaint:", error);
        toast.error("Fout bij het bijwerken van klacht");
        return undefined;
      }

      toast.success("Klacht status bijgewerkt");

      if (status === "resolved" || status === "closed") {
        const complaint = complaints.find((c) => c.id === complaintId);
        if (complaint && user && selectedPharmacy) {
          await logAuditEvent({
            pharmacy_id: selectedPharmacy.id,
            user_id: user.id,
            user_name: user.full_name,
            action: status,
            entity_type: "complaint",
            entity_id: complaintId,
            entity_label: complaint.complaint_number,
          });
        }
      }

      fetchComplaints();
    } catch (error) {
      console.error("Error updating complaint:", error);
      toast.error("Fout bij het bijwerken van klacht");
    }
  };

  const saveCapa = async (complaintId: string) => {
    if (!selectedPharmacy) return;
    setSavingCapa(true);
    try {
      const responsibleUser = capaForm.capa_responsible_id
        ? users.find((u) => u.id === capaForm.capa_responsible_id)
        : null;

      const updateData: Record<string, unknown> = {
        root_cause: capaForm.root_cause || null,
        corrective_action: capaForm.corrective_action || null,
        preventive_action: capaForm.preventive_action || null,
        capa_status: capaForm.capa_status,
        capa_due_date: capaForm.capa_due_date || null,
        capa_responsible_id: capaForm.capa_responsible_id || null,
        capa_responsible_name: responsibleUser?.full_name || null,
        updated_at: new Date().toISOString(),
      };

      if (capaForm.capa_status === "completed") {
        updateData.capa_completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from(Tables.COMPLAINTS)
        .update(updateData)
        .eq("id", complaintId);

      if (error) throw error;

      toast.success("CAPA opgeslagen");
      fetchComplaints();
      // Refresh selectedComplaint
      setSelectedComplaint((prev) =>
        prev
          ? {
              ...prev,
              ...updateData,
              capa_responsible_name: responsibleUser?.full_name,
            }
          : prev,
      );
    } catch (err) {
      console.error("Error saving CAPA:", err);
      toast.error("Fout bij opslaan CAPA");
    } finally {
      setSavingCapa(false);
    }
  };

  const deleteComplaint = async (complaintId: string) => {
    try {
      const { error } = await supabase
        .from(Tables.COMPLAINTS)
        .delete()
        .eq("id", complaintId);

      if (error) {
        console.error("Error deleting complaint:", error);
        toast.error("Fout bij het verwijderen van klacht");
        return;
      }

      toast.success("Klacht verwijderd");
      fetchComplaints();
    } catch (error) {
      console.error("Error deleting complaint:", error);
      toast.error("Fout bij het verwijderen van klacht");
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleExportCsv = () => {
    const headers = [
      "Klachtnummer",
      "Categorie",
      "Ernst",
      "Status",
      "Klantnaam",
      "Omschrijving",
      "Toegewezen aan",
      "Aangemaakt op",
      "Opgelost op",
    ];
    const rows = filteredComplaints.map((c) => [
      c.complaint_number,
      c.category,
      c.severity,
      c.status,
      c.customer_name || "",
      c.description,
      c.assigned_to_name || "",
      format(parseISO(c.created_at), "dd-MM-yyyy"),
      c.resolved_at ? format(parseISO(c.resolved_at), "dd-MM-yyyy") : "",
    ]);
    exportToCsv(
      `klachten_${format(new Date(), "yyyy-MM-dd")}.csv`,
      headers,
      rows,
    );
    toast.success("CSV geëxporteerd");
  };

  const getSeverityIcon = (severity: ComplaintWithUser["severity"]) => {
    switch (severity) {
      case "critical":
        return ExclamationTriangleIcon;
      case "high":
        return ExclamationTriangleIcon;
      case "medium":
        return ClockIcon;
      default:
        return CheckCircleIcon;
    }
  };

  const getSeverityColor = (severity: ComplaintWithUser["severity"]) => {
    switch (severity) {
      case "critical":
        return "text-red-600 bg-red-100";
      case "high":
        return "text-orange-600 bg-orange-100";
      case "medium":
        return "text-yellow-600 bg-yellow-100";
      default:
        return "text-green-600 bg-green-100";
    }
  };

  const getStatusColor = (status: ComplaintWithUser["status"]) => {
    switch (status) {
      case "open":
        return "text-red-600 bg-red-100";
      case "investigating":
        return "text-blue-600 bg-blue-100";
      case "resolved":
        return "text-green-600 bg-green-100";
      case "closed":
        return "text-gray-600 bg-gray-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getCategoryDisplayName = (category: ComplaintWithUser["category"]) => {
    switch (category) {
      case "product":
        return "Product";
      case "service":
        return "Service";
      case "staff":
        return "Personeel";
      default:
        return "Overig";
    }
  };

  const getSeverityDisplayName = (severity: ComplaintWithUser["severity"]) => {
    switch (severity) {
      case "critical":
        return "Kritiek";
      case "high":
        return "Hoog";
      case "medium":
        return "Gemiddeld";
      default:
        return "Laag";
    }
  };

  const getStatusDisplayName = (status: ComplaintWithUser["status"]) => {
    switch (status) {
      case "open":
        return "Open";
      case "investigating":
        return "Onderzoek";
      case "resolved":
        return "Opgelost";
      case "closed":
        return "Gesloten";
      default:
        return "Onbekend";
    }
  };

  // Filter complaints
  const filteredComplaints = complaints.filter((complaint) => {
    const matchesSearch =
      filters.search === "" ||
      complaint.complaint_number
        .toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      complaint.customer_name
        ?.toLowerCase()
        .includes(filters.search.toLowerCase()) ||
      complaint.description
        .toLowerCase()
        .includes(filters.search.toLowerCase());

    const matchesStatus =
      filters.status === "all" || complaint.status === filters.status;
    const matchesSeverity =
      filters.severity === "all" || complaint.severity === filters.severity;
    const matchesCategory =
      filters.category === "all" || complaint.category === filters.category;
    const matchesAssignedTo =
      filters.assignedTo === "all" ||
      complaint.assigned_to === filters.assignedTo;

    return (
      matchesSearch &&
      matchesStatus &&
      matchesSeverity &&
      matchesCategory &&
      matchesAssignedTo
    );
  });

  // --- Trend analytics computed from all complaints ---
  const trendData = useMemo(() => {
    // Last 12 months volume
    const now = new Date();
    const monthlyVolume = Array.from({ length: 12 }, (_, i) => {
      const month = subMonths(now, 11 - i);
      const start = startOfMonth(month);
      const end = endOfMonth(month);
      const count = complaints.filter((c) => {
        const d = parseISO(c.created_at);
        return isWithinInterval(d, { start, end });
      }).length;
      return { month: format(month, "MMM yy", { locale: nl }), count };
    });

    // By category
    const categoryCounts: Record<string, number> = { product: 0, service: 0, staff: 0, other: 0 };
    complaints.forEach((c) => { categoryCounts[c.category] = (categoryCounts[c.category] || 0) + 1; });
    const byCategory = [
      { name: "Product", value: categoryCounts.product, color: "#6366f1" },
      { name: "Service", value: categoryCounts.service, color: "#22d3ee" },
      { name: "Personeel", value: categoryCounts.staff, color: "#f59e0b" },
      { name: "Overig", value: categoryCounts.other, color: "#94a3b8" },
    ].filter((d) => d.value > 0);

    // By severity
    const severityCounts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    complaints.forEach((c) => { severityCounts[c.severity] = (severityCounts[c.severity] || 0) + 1; });
    const bySeverity = [
      { name: "Kritiek", value: severityCounts.critical, color: "#dc2626" },
      { name: "Hoog", value: severityCounts.high, color: "#f97316" },
      { name: "Gemiddeld", value: severityCounts.medium, color: "#f59e0b" },
      { name: "Laag", value: severityCounts.low, color: "#22c55e" },
    ].filter((d) => d.value > 0);

    // Top root causes (non-empty)
    const rootCauseCounts: Record<string, number> = {};
    complaints.forEach((c) => {
      if (c.root_cause && c.root_cause.trim()) {
        const key = c.root_cause.trim().toLowerCase();
        rootCauseCounts[key] = (rootCauseCounts[key] || 0) + 1;
      }
    });
    const topRootCauses = Object.entries(rootCauseCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([cause, count]) => ({ cause: cause.charAt(0).toUpperCase() + cause.slice(1), count }));

    // CAPA status breakdown
    const capaCounts: Record<string, number> = { not_required: 0, open: 0, in_progress: 0, completed: 0, none: 0 };
    complaints.forEach((c) => {
      const key = c.capa_status || "none";
      capaCounts[key] = (capaCounts[key] || 0) + 1;
    });
    const capaStatus = [
      { name: "Niet vereist", value: capaCounts.not_required, color: "#94a3b8" },
      { name: "Open", value: capaCounts.open, color: "#ef4444" },
      { name: "In uitvoering", value: capaCounts.in_progress, color: "#f59e0b" },
      { name: "Voltooid", value: capaCounts.completed, color: "#22c55e" },
      { name: "Geen CAPA", value: capaCounts.none, color: "#e2e8f0" },
    ].filter((d) => d.value > 0);

    return { monthlyVolume, byCategory, bySeverity, topRootCauses, capaStatus };
  }, [complaints]);

  if (loading) {
    return (
      <div className="p-6 sm:p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Klachtenbeheer</h1>
            <p className="text-gray-600 mt-1">
              Beheer en volg klachten van klanten en medewerkers
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {activeTab === "list" && (
              <>
                <button
                  onClick={handleExportCsv}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  title="Exporteer gefilterde klachten naar CSV"
                >
                  <ArrowDownTrayIcon className="-ml-1 mr-2 h-4 w-4" />
                  CSV
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Nieuwe Klacht
                </button>
              </>
            )}
          </div>
        </div>
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab("list")}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center space-x-2 ${
                activeTab === "list"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <ListBulletIcon className="h-4 w-4" />
              <span>Klachtenlijst</span>
            </button>
            <button
              onClick={() => setActiveTab("trends")}
              className={`py-2 px-1 border-b-2 text-sm font-medium flex items-center space-x-2 ${
                activeTab === "trends"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <ChartBarIcon className="h-4 w-4" />
              <span>Trendanalyse</span>
              <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                {complaints.length}
              </span>
            </button>
          </nav>
        </div>
      </div>

      {activeTab === "trends" && (
        <div className="space-y-6">
          {/* Monthly volume bar chart */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Klachten per maand (12 maanden)</h2>
            {complaints.length === 0 ? (
              <p className="text-gray-500 text-sm">Nog geen klachten geregistreerd.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trendData.monthlyVolume} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => [v ?? 0, "Klachten"]} />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Category + Severity pie charts side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Klachten per categorie</h2>
              {trendData.byCategory.length === 0 ? (
                <p className="text-gray-500 text-sm">Geen data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={trendData.byCategory} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`} labelLine={false}>
                      {trendData.byCategory.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [v ?? 0, "klachten"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Klachten per ernst</h2>
              {trendData.bySeverity.length === 0 ? (
                <p className="text-gray-500 text-sm">Geen data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={trendData.bySeverity} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`} labelLine={false}>
                      {trendData.bySeverity.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [v ?? 0, "klachten"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Root causes + CAPA status side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Meest voorkomende oorzaken</h2>
              {trendData.topRootCauses.length === 0 ? (
                <p className="text-gray-500 text-sm">Geen oorzaken geregistreerd.</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={trendData.topRootCauses} layout="vertical" margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="cause" width={140} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [v ?? 0, "klachten"]} />
                    <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">CAPA status overzicht</h2>
              {trendData.capaStatus.length === 0 ? (
                <p className="text-gray-500 text-sm">Geen data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={trendData.capaStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${Math.round((percent ?? 0) * 100)}%`} labelLine={false}>
                      {trendData.capaStatus.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [v ?? 0, "klachten"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "list" && (
      <>
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Zoeken
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                placeholder="Klachtnummer, klant, beschrijving..."
                className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle statussen</option>
              <option value="open">Open</option>
              <option value="investigating">Onderzoek</option>
              <option value="resolved">Opgelost</option>
              <option value="closed">Gesloten</option>
            </select>
          </div>

          {/* Severity Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ernst
            </label>
            <select
              value={filters.severity}
              onChange={(e) =>
                setFilters({ ...filters, severity: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle ernst</option>
              <option value="critical">Kritiek</option>
              <option value="high">Hoog</option>
              <option value="medium">Gemiddeld</option>
              <option value="low">Laag</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categorie
            </label>
            <select
              value={filters.category}
              onChange={(e) =>
                setFilters({ ...filters, category: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle categorieën</option>
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="staff">Personeel</option>
              <option value="other">Overig</option>
            </select>
          </div>

          {/* Assigned To Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Toegewezen aan
            </label>
            <select
              value={filters.assignedTo}
              onChange={(e) =>
                setFilters({ ...filters, assignedTo: e.target.value })
              }
              className="block w-full border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            >
              <option value="all">Alle gebruikers</option>
              <option value="">Niet toegewezen</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Complaints List */}
      <div className="space-y-4">
        {filteredComplaints.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Geen klachten gevonden
            </h3>
            <p className="text-gray-600 mb-4">
              {filters.search ||
              filters.status !== "all" ||
              filters.severity !== "all" ||
              filters.category !== "all" ||
              filters.assignedTo !== "all"
                ? "Geen klachten die voldoen aan de filters."
                : "Er zijn nog geen klachten geregistreerd."}
            </p>
            {filters.search ||
            filters.status !== "all" ||
            filters.severity !== "all" ||
            filters.category !== "all" ||
            filters.assignedTo !== "all" ? (
              <button
                onClick={() =>
                  setFilters({
                    status: "all",
                    severity: "all",
                    category: "all",
                    assignedTo: "all",
                    search: "",
                  })
                }
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                Filters wissen
              </button>
            ) : (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Eerste klacht toevoegen
              </button>
            )}
          </div>
        ) : (
          filteredComplaints.slice((page - 1) * COMPLAINTS_PAGE_SIZE, page * COMPLAINTS_PAGE_SIZE).map((complaint) => {
            const SeverityIcon = getSeverityIcon(complaint.severity);

            return (
              <div
                key={complaint.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div
                        className={`p-2 rounded-lg ${getSeverityColor(complaint.severity)}`}
                      >
                        <SeverityIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {complaint.complaint_number}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(complaint.status)}`}
                          >
                            {getStatusDisplayName(complaint.status)}
                          </span>
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(complaint.severity)}`}
                          >
                            {getSeverityDisplayName(complaint.severity)}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {getCategoryDisplayName(complaint.category)}
                          </span>
                          {complaint.capa_status && complaint.capa_status !== "not_required" && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              complaint.capa_status === "completed"
                                ? "bg-green-100 text-green-800"
                                : complaint.capa_status === "in_progress"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-orange-100 text-orange-800"
                            }`}>
                              CAPA: {complaint.capa_status === "completed" ? "Voltooid" : complaint.capa_status === "in_progress" ? "Loopt" : "Open"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      {complaint.customer_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium">Klant:</span>
                          <span className="ml-1">
                            {complaint.customer_name}
                          </span>
                        </div>
                      )}

                      {complaint.customer_contact && (
                        <div className="flex items-center text-sm text-gray-600">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium">Contact:</span>
                          <span className="ml-1">
                            {complaint.customer_contact}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span className="font-medium">Aangemaakt:</span>
                        <span className="ml-1">
                          {format(
                            parseISO(complaint.created_at),
                            "dd MMM yyyy",
                            { locale: nl },
                          )}
                        </span>
                      </div>

                      {complaint.assigned_to_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="font-medium">Toegewezen aan:</span>
                          <span className="ml-1">
                            {complaint.assigned_to_name}
                          </span>
                        </div>
                      )}
                    </div>

                    <p className="text-gray-700 mb-4 line-clamp-3">
                      {complaint.description}
                    </p>

                    {complaint.resolution && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                        <div className="flex items-center mb-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-sm font-medium text-green-800">
                            Oplossing
                          </span>
                          {complaint.resolved_at && (
                            <span className="text-xs text-green-600 ml-2">
                              (
                              {format(
                                parseISO(complaint.resolved_at),
                                "dd MMM yyyy",
                                { locale: nl },
                              )}
                              )
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-green-700">
                          {complaint.resolution}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedComplaint(complaint);
                        setCapaForm({
                          root_cause: complaint.root_cause || "",
                          corrective_action: complaint.corrective_action || "",
                          preventive_action: complaint.preventive_action || "",
                          capa_status: complaint.capa_status || "open",
                          capa_due_date: complaint.capa_due_date || "",
                          capa_responsible_id: complaint.capa_responsible_id || "",
                        });
                        setShowComplaintModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                      title="Bekijk details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>

                    {complaint.status !== "resolved" &&
                      complaint.status !== "closed" && (
                        <div className="flex space-x-1">
                          {complaint.status === "open" && (
                            <button
                              onClick={() =>
                                updateComplaintStatus(
                                  complaint.id,
                                  "investigating",
                                )
                              }
                              className="px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-full"
                            >
                              Start onderzoek
                            </button>
                          )}

                          {complaint.status === "investigating" && (
                            <button
                              onClick={() => {
                                const resolution = prompt(
                                  "Voer een oplossing in:",
                                );
                                if (resolution) {
                                  updateComplaintStatus(
                                    complaint.id,
                                    "resolved",
                                    resolution,
                                  );
                                }
                              }}
                              className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded-full"
                            >
                              Markeer als opgelost
                            </button>
                          )}
                        </div>
                      )}

                    {complaint.status === "resolved" && (
                      <button
                        onClick={() =>
                          updateComplaintStatus(complaint.id, "closed")
                        }
                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full"
                      >
                        Sluit klacht
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <Pagination
          page={page}
          pageSize={COMPLAINTS_PAGE_SIZE}
          total={filteredComplaints.length}
          onPageChange={setPage}
        />
      </div>

      {/* Create Complaint Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Nieuwe Klacht Registreren
                </h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Naam klant
                    </label>
                    <input
                      type="text"
                      value={newComplaint.customer_name}
                      onChange={(e) =>
                        setNewComplaint({
                          ...newComplaint,
                          customer_name: e.target.value,
                        })
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Naam van de klant (optioneel)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact informatie
                    </label>
                    <input
                      type="text"
                      value={newComplaint.customer_contact}
                      onChange={(e) =>
                        setNewComplaint({
                          ...newComplaint,
                          customer_contact: e.target.value,
                        })
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                      placeholder="Telefoon of email (optioneel)"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categorie
                    </label>
                    <select
                      value={newComplaint.category}
                      onChange={(e) =>
                        setNewComplaint({
                          ...newComplaint,
                          category: e.target.value as
                            | "product"
                            | "service"
                            | "staff"
                            | "other",
                        })
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="product">Product</option>
                      <option value="service">Service</option>
                      <option value="staff">Personeel</option>
                      <option value="other">Overig</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ernst
                    </label>
                    <select
                      value={newComplaint.severity}
                      onChange={(e) =>
                        setNewComplaint({
                          ...newComplaint,
                          severity: e.target.value as
                            | "low"
                            | "medium"
                            | "high"
                            | "critical",
                        })
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="low">Laag</option>
                      <option value="medium">Gemiddeld</option>
                      <option value="high">Hoog</option>
                      <option value="critical">Kritiek</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Toewijzen aan
                    </label>
                    <select
                      value={newComplaint.assigned_to}
                      onChange={(e) =>
                        setNewComplaint({
                          ...newComplaint,
                          assigned_to: e.target.value,
                        })
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    >
                      <option value="">Niet toegewezen</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.full_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschrijving *
                  </label>
                  <textarea
                    value={newComplaint.description}
                    onChange={(e) =>
                      setNewComplaint({
                        ...newComplaint,
                        description: e.target.value,
                      })
                    }
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="Gedetailleerde beschrijving van de klacht..."
                    required
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={createComplaint}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                >
                  Klacht Registreren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complaint Detail Modal */}
      {showComplaintModal && selectedComplaint && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Klacht Details - {selectedComplaint.complaint_number}
                </h3>
                <button
                  onClick={() => setShowComplaintModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Status and Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Status
                    </h4>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedComplaint.status)}`}
                    >
                      {getStatusDisplayName(selectedComplaint.status)}
                    </span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Ernst
                    </h4>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(selectedComplaint.severity)}`}
                    >
                      {getSeverityDisplayName(selectedComplaint.severity)}
                    </span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Categorie
                    </h4>
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {getCategoryDisplayName(selectedComplaint.category)}
                    </span>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Klant Informatie
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Naam: </span>
                      <span className="text-gray-600">
                        {selectedComplaint.customer_name || "Niet opgegeven"}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Contact:{" "}
                      </span>
                      <span className="text-gray-600">
                        {selectedComplaint.customer_contact || "Niet opgegeven"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Beschrijving
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {selectedComplaint.description}
                    </p>
                  </div>
                </div>

                {/* Resolution */}
                {selectedComplaint.resolution && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Oplossing
                    </h4>
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <p className="text-green-700 whitespace-pre-wrap">
                        {selectedComplaint.resolution}
                      </p>
                      {selectedComplaint.resolved_at && (
                        <p className="text-xs text-green-600 mt-2">
                          Opgelost op:{" "}
                          {format(
                            parseISO(selectedComplaint.resolved_at),
                            "dd MMMM yyyy 'om' HH:mm",
                            { locale: nl },
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Assignment and Dates */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Toewijzing
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">
                          Aangemaakt door:{" "}
                        </span>
                        <span className="text-gray-600">
                          {selectedComplaint.created_by_name}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">
                          Toegewezen aan:{" "}
                        </span>
                        <span className="text-gray-600">
                          {selectedComplaint.assigned_to_name ||
                            "Niet toegewezen"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      Belangrijke Datums
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">
                          Aangemaakt:{" "}
                        </span>
                        <span className="text-gray-600">
                          {format(
                            parseISO(selectedComplaint.created_at),
                            "dd MMMM yyyy 'om' HH:mm",
                            { locale: nl },
                          )}
                        </span>
                      </div>
                      {selectedComplaint.resolved_at && (
                        <div>
                          <span className="font-medium text-gray-700">
                            Opgelost:{" "}
                          </span>
                          <span className="text-gray-600">
                            {format(
                              parseISO(selectedComplaint.resolved_at),
                              "dd MMMM yyyy 'om' HH:mm",
                              { locale: nl },
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* CAPA Section */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">CAPA</span>
                    Corrigerende &amp; Preventieve Maatregelen
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Grondoorzaak (Root Cause)</label>
                      <textarea
                        rows={2}
                        value={capaForm.root_cause}
                        onChange={(e) => setCapaForm((f) => ({ ...f, root_cause: e.target.value }))}
                        placeholder="Wat was de onderliggende oorzaak?"
                        className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Corrigerende maatregel *</label>
                      <textarea
                        rows={2}
                        value={capaForm.corrective_action}
                        onChange={(e) => setCapaForm((f) => ({ ...f, corrective_action: e.target.value }))}
                        placeholder="Wat is er gedaan om het probleem op te lossen?"
                        className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Preventieve maatregel</label>
                      <textarea
                        rows={2}
                        value={capaForm.preventive_action}
                        onChange={(e) => setCapaForm((f) => ({ ...f, preventive_action: e.target.value }))}
                        placeholder="Wat wordt gedaan om herhaling te voorkomen?"
                        className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">CAPA Status</label>
                        <select
                          value={capaForm.capa_status}
                          onChange={(e) => setCapaForm((f) => ({ ...f, capa_status: e.target.value as typeof f.capa_status }))}
                          className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="not_required">Niet vereist</option>
                          <option value="open">Open</option>
                          <option value="in_progress">Loopt</option>
                          <option value="completed">Voltooid</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Deadline</label>
                        <input
                          type="date"
                          value={capaForm.capa_due_date}
                          onChange={(e) => setCapaForm((f) => ({ ...f, capa_due_date: e.target.value }))}
                          className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Verantwoordelijke</label>
                        <select
                          value={capaForm.capa_responsible_id}
                          onChange={(e) => setCapaForm((f) => ({ ...f, capa_responsible_id: e.target.value }))}
                          className="block w-full border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">— Selecteer —</option>
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.full_name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {selectedComplaint.capa_completed_at && (
                      <p className="text-xs text-green-600">
                        Voltooid op {format(parseISO(selectedComplaint.capa_completed_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                      </p>
                    )}
                    <div className="flex justify-end">
                      <button
                        onClick={() => saveCapa(selectedComplaint.id)}
                        disabled={savingCapa}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                      >
                        {savingCapa ? "Opslaan…" : "CAPA opslaan"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <button
                    onClick={() => {
                      setConfirmDelete(selectedComplaint.id);
                      setShowComplaintModal(false);
                    }}
                    className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    Verwijder klacht
                  </button>

                  <div className="flex space-x-3">
                    {selectedComplaint.status === "open" && (
                      <button
                        onClick={() => {
                          updateComplaintStatus(
                            selectedComplaint.id,
                            "investigating",
                          );
                          setShowComplaintModal(false);
                        }}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                      >
                        Start Onderzoek
                      </button>
                    )}

                    {selectedComplaint.status === "investigating" && (
                      <button
                        onClick={() => {
                          const resolution = prompt("Voer een oplossing in:");
                          if (resolution) {
                            updateComplaintStatus(
                              selectedComplaint.id,
                              "resolved",
                              resolution,
                            );
                            setShowComplaintModal(false);
                          }
                        }}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                      >
                        Markeer als Opgelost
                      </button>
                    )}

                    {selectedComplaint.status === "resolved" && (
                      <button
                        onClick={() => {
                          updateComplaintStatus(selectedComplaint.id, "closed");
                          setShowComplaintModal(false);
                        }}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700"
                      >
                        Sluit Klacht
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title="Klacht verwijderen"
        message="Weet je zeker dat je deze klacht wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
        confirmLabel="Verwijderen"
        onConfirm={() => confirmDelete && deleteComplaint(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
      </>
      )}
    </div>
  );
};

export default Complaints;
